import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import userService from '../services/userService';
import sponsorService from '../services/sponsorService';
import studyService from '../services/studyService';
import siteService from '../services/siteService';
import { PlusCircle, User, Users, Search, RefreshCw, MapPin } from 'lucide-react';
import AddUserModal from '../components/User/AddUserModal';
import PageHeader from '../components/Shared/PageHeader';
import toast from 'react-hot-toast';

// Define role hierarchy for permissions
const ROLE_HIERARCHY = {
  'program_admin': 1, // Highest level
  'project_admin': 2,
  'data_manager': 3,
  'study_team': 3,
  'study_coordinator': 4,
  'investigator': 5,
  'cra': 5,
};

// Roles that can add users
const CAN_ADD_USERS = ['program_admin', 'project_admin', 'study_team'];

const UserManagement = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [studies, setStudies] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTab, setSelectedTab] = useState('users');
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [sponsorId, setSponsorId] = useState(null);
  
  // Log user information for debugging
  useEffect(() => {
    console.log('UserManagement: Current user context:', user);
    
    // Try to determine the user's sponsor ID from various possible structures
    let sponsorId = null;
    
    if (user?.sponsor_id) {
      sponsorId = user.sponsor_id;
      console.log('UserManagement: Found sponsor_id directly:', sponsorId);
    } else if (user?.sponsor?.id) {
      sponsorId = user.sponsor.id;
      console.log('UserManagement: Found sponsor via sponsor.id:', sponsorId);
    } else if (user?.sponsors && user.sponsors.length > 0) {
      sponsorId = user.sponsors[0].id;
      console.log('UserManagement: Found sponsor via sponsors array:', sponsorId);
    } else {
      console.warn('UserManagement: No sponsor found for current user!');
    }
    
    // Store the sponsor ID in component state for use in the modal
    setSponsorId(sponsorId);
  }, [user]);
  
  // Check if current user can add users
  const canAddUsers = user && CAN_ADD_USERS.includes(user.role);
  
  // Calculate available roles based on current user's role
  useEffect(() => {
    if (user && user.role) {
      const currentUserLevel = ROLE_HIERARCHY[user.role] || 999;
      const roles = userService.getRoles(user).filter(role => {
        // Only show roles that are lower in hierarchy than current user
        const roleLevel = ROLE_HIERARCHY[role.value] || 999;
        return roleLevel > currentUserLevel;
      });
      setAvailableRoles(roles);
    }
  }, [user]);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers();
      // The list comes back paged ({ results }) on the deployed settings and as a plain
      // array locally. Names live under profile, and the endpoint only returns active
      // accounts, so a row without is_active is an active user.
      const rows = Array.isArray(response) ? response : response?.results || [];
      setUsers(rows.map((u) => ({
        ...u,
        first_name: u.first_name ?? u.profile?.first_name ?? '',
        last_name: u.last_name ?? u.profile?.last_name ?? '',
        is_active: u.is_active ?? true,
      })));
    } catch (error) {
      toast.error('Failed to load users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSponsors = async () => {
    try {
      const response = await sponsorService.getSponsors();
      setSponsors(response);
    } catch (error) {
      toast.error('Failed to load sponsors');
      console.error('Error fetching sponsors:', error);
    }
  };
  
  const fetchStudies = async () => {
    try {
      const response = await studyService.getStudies();
      setStudies(response);
    } catch (error) {
      toast.error('Failed to load studies');
      console.error('Error fetching studies:', error);
    }
  };
  
  const fetchSites = async () => {
    try {
      const response = await siteService.getAllSites();
      setSites(response.sites || response || []);
    } catch (error) {
      toast.error('Failed to load sites');
      console.error('Error fetching sites:', error);
    }
  };
  
  useEffect(() => {
    fetchUsers();
    fetchSponsors();
    fetchStudies();
    fetchSites();
  }, []);
  
  const handleAddUser = () => {
    setIsAddUserModalOpen(true);
  };
  
  const handleUserAdded = () => {
    fetchUsers();
    toast.success('User added successfully!');
  };
  
  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.first_name?.toLowerCase().includes(query) ||
      user.last_name?.toLowerCase().includes(query) ||
      user.role?.toLowerCase().includes(query)
    );
  });
  
  const handleAssignSponsor = async (userId, sponsorId) => {
    try {
      await userService.assignSponsor(userId, sponsorId);
      fetchUsers(); // Refresh user list
      toast.success('Sponsor assigned successfully');
    } catch (error) {
      toast.error('Failed to assign sponsor');
      console.error('Error assigning sponsor:', error);
    }
  };
  
  const handleAssignStudy = async (userId, studyId) => {
    try {
      await userService.assignStudy(userId, studyId);
      fetchUsers(); // Refresh user list
      toast.success('Study assigned successfully');
    } catch (error) {
      toast.error('Failed to assign study');
      console.error('Error assigning study:', error);
    }
  };
  
  const handleAssignSite = async (userId, siteId) => {
    try {
      await userService.assignSite(userId, siteId);
      fetchUsers(); // Refresh user list
      toast.success('Site assigned successfully');
    } catch (error) {
      toast.error('Failed to assign site');
      console.error('Error assigning site:', error);
    }
  };
  
  // Helper function to get role label
  const getRoleLabel = (roleValue) => {
    const role = userService.getRoles(user).find(r => r.value === roleValue);
    return role ? role.label : roleValue;
  };
  
  return (
    <div>
      {/* Page Header */}
      <PageHeader
        icon={Users}
        title="User Management"
        subtitle="Manage users, assign them to sponsors and studies"
      />
      
      <div>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input w-64 pl-10"
            />
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => fetchUsers()}
              className="btn btn-secondary"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
            
            {canAddUsers && (
              <button
                onClick={handleAddUser}
                className="btn btn-primary"
              >
                <PlusCircle className="h-4 w-4" />
                <span>Add User</span>
              </button>
            )}
          </div>
        </div>
        
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="users" className="flex-1">User List</TabsTrigger>
            <TabsTrigger value="sponsors" className="flex-1">Sponsor Assignments</TabsTrigger>
            <TabsTrigger value="studies" className="flex-1">Study Assignments</TabsTrigger>
            <TabsTrigger value="sites" className="flex-1">Site Assignments</TabsTrigger>
          </TabsList>
          
          <TabsContent value="users">
            <div className="card overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th scope="col" className="th-cell">User</th>
                    <th scope="col" className="th-cell">Email</th>
                    <th scope="col" className="th-cell">Role</th>
                    <th scope="col" className="th-cell">Status</th>
                    <th scope="col" className="th-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="td-cell text-center text-gray-500 dark:text-gray-400">
                        Loading users...
                      </td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="td-cell text-center text-gray-500 dark:text-gray-400">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((userItem) => (
                      <tr key={userItem.id} className="tr-interactive">
                        <td className="td-cell whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                              <User className="h-4 w-4" />
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{userItem.first_name} {userItem.last_name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="td-cell whitespace-nowrap text-gray-500 dark:text-gray-400">{userItem.email}</td>
                        <td className="td-cell whitespace-nowrap text-gray-500 dark:text-gray-400">{getRoleLabel(userItem.role)}</td>
                        <td className="td-cell whitespace-nowrap">
                          <span className={`badge ${userItem.is_active ? 'badge-success' : 'badge-danger'}`}>
                            {userItem.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="td-cell whitespace-nowrap">
                          <button
                            onClick={() => setSelectedUser(userItem)}
                            className="link mr-3 text-sm"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
          
          <TabsContent value="sponsors">
            <div className="card p-6">
              <h3 className="mb-1.5 text-base font-semibold text-gray-900 dark:text-gray-100">Sponsor Assignments</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This feature allows you to assign users to specific sponsors, giving them the appropriate access rights.
              </p>
              
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Select User</h4>
                  <select 
                    className="input"
                    value={selectedUser?.id || ""}
                    onChange={(e) => {
                      const userId = e.target.value;
                      const user = users.find(u => u.id === userId);
                      setSelectedUser(user);
                    }}
                  >
                    <option value="">Select a user</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Assign to Sponsor</h4>
                  <select 
                    className="input"
                    onChange={(e) => {
                      if (selectedUser) {
                        handleAssignSponsor(selectedUser.id, e.target.value);
                      }
                    }}
                  >
                    <option value="">Select a sponsor</option>
                    {sponsors.map(sponsor => (
                      <option key={sponsor.id} value={sponsor.id}>
                        {sponsor.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="studies">
            <div className="card p-6">
              <h3 className="mb-1.5 text-base font-semibold text-gray-900 dark:text-gray-100">Study Assignments</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This feature allows you to assign users to specific studies, determining which data they can access.
              </p>
              
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Select User</h4>
                  <select 
                    className="input"
                    value={selectedUser?.id || ""}
                    onChange={(e) => {
                      const userId = e.target.value;
                      const user = users.find(u => u.id === userId);
                      setSelectedUser(user);
                    }}
                  >
                    <option value="">Select a user</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Assign to Study</h4>
                  <select 
                    className="input"
                    onChange={(e) => {
                      if (selectedUser) {
                        handleAssignStudy(selectedUser.id, e.target.value);
                      }
                    }}
                  >
                    <option value="">Select a study</option>
                    {studies.map(study => (
                      <option key={study.id} value={study.id}>
                        {study.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="sites">
            <div className="card p-6">
              <h3 className="mb-1.5 text-base font-semibold text-gray-900 dark:text-gray-100">Site Assignments</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Assign users to specific sites. This is important for Investigators and Study Coordinators who need site-level access.
              </p>
              
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Select User</h4>
                  <select 
                    className="input"
                    value={selectedUser?.id || ""}
                    onChange={(e) => {
                      const userId = e.target.value;
                      const user = users.find(u => u.id === userId);
                      setSelectedUser(user);
                    }}
                  >
                    <option value="">Select a user</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Assign to Site</h4>
                  <select 
                    className="input"
                    onChange={(e) => {
                      if (selectedUser) {
                        handleAssignSite(selectedUser.id, e.target.value);
                      }
                    }}
                  >
                    <option value="">Select a site</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>
                        {site.site_code} - {site.site_name} ({site.study_name || 'No Study'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="mt-4 rounded-lg border border-warning-500/30 bg-warning-50 px-3.5 py-3 dark:bg-warning-500/10">
                <p className="text-sm text-warning-800 dark:text-warning-400">
                  <MapPin className="mr-1 inline h-4 w-4 align-[-2px]" />
                  <strong>Note:</strong> Investigators and Study Coordinators access the system at the site level, not the study level.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Add User Modal */}
      {canAddUsers && (
        <AddUserModal
          isOpen={isAddUserModalOpen}
          onClose={() => setIsAddUserModalOpen(false)}
          onSuccess={handleUserAdded}
          availableRoles={availableRoles}
          sponsorId={sponsorId} 
        />
      )}
    </div>
  );
};

export default UserManagement;
